import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import EmptyContainer from '../../common/EmptyContainer';
import Loader from '../../common/Loader';
import Empty from '../Empty';
import ML360Result from './ML360Result';
import SartResult from './SartResult';
import SelfDeterminationResult from './SelfDeterminationResult';
import SpatialPlanningResult from './SpatialPlanningResult';
import TeamResilienceResult from './TeamResilienceResult';
import VisualReactionResult from './VisualReactionResult';
import { useFetchResultQuery } from './shared/Results.api';
import { convertShortLabel, type ML360Statistic } from './shared/utils';
import { SartErrorType, VisualReactionErrorType } from '@/apolloClient.types';
import Drawer from '@/components/common/Drawer';
import useDateFormats from '@/utils/useDateFormats';

interface ResultDrawerProps {
    open: boolean;
    onClose: () => void;
    resultId: string;
}

const ResultDrawer = ({ open, onClose, resultId }: ResultDrawerProps) => {
    const { t } = useTranslation('assessmentResultManagementPage');
    const { formatDate } = useDateFormats();

    const { data, loading, error } = useFetchResultQuery({
        fetchPolicy: 'cache-and-network',
        variables: { id: resultId },
    });

    const result = useMemo(() => data?.assessmentResult?.result || null, [data?.assessmentResult?.result]);

    const renderResult = useMemo(() => {
        switch (result?.__typename) {
            case 'SartResult': {
                const { correct, incorrect, missed } = result.trials.reduce(
                    (
                        acc: {
                            correct: { index: number; responseTimeMs: number }[];
                            incorrect: { index: number; responseTimeMs: number }[];
                            missed: { index: number; responseTimeMs: number }[];
                        },
                        answer
                    ) => {
                        // Include ALL data points in chart data for complete visualization
                        if (answer.isCorrect) {
                            acc.correct.push({ index: answer.index + 1, responseTimeMs: answer.responseTimeMs });
                        } else if (answer.errorType && answer.errorType === SartErrorType.Incorrect) {
                            acc.incorrect.push({ index: answer.index + 1, responseTimeMs: answer.responseTimeMs });
                        } else if (answer.errorType && answer.errorType === SartErrorType.Missed) {
                            acc.missed.push({ index: answer.index + 1, responseTimeMs: answer.responseTimeMs });
                        }
                        return acc;
                    },
                    {
                        correct: [],
                        incorrect: [],
                        missed: [],
                    }
                );

                const line = {
                    name: 'line',
                    data: result.trials.map(answer => ({
                        index: answer.index + 1,
                        responseTimeMs: answer.responseTimeMs,
                    })),
                };

                const correctHits = result.trials.filter(answer => answer.isCorrect).length;
                const accuracy = Number(((correctHits / result.trials.length) * 100).toFixed(2));

                const statistics = {
                    series: [
                        { name: 'correct', data: correct },
                        { name: 'incorrect', data: incorrect },
                        { name: 'missed', data: missed },
                    ],
                    line,
                    accuracy,
                    averageResponseTimeMs: result.averageResponseTimeMs,
                    fastestResponseTimeMs: result.fastestResponseTimeMs,
                };

                return <SartResult statistics={statistics} questionLength={result.trials.length} />;
            }
            case 'SpatialPlanningResult': {
                const data = {
                    correctAnswers: result.correctAnswers,
                    incorrectAnswers: result.incorrectAnswers,
                    accuracy: result.accuracy,
                    averageResponseTimeMs: result.averageResponseTimeMs || 0,
                    fastestResponseTimeMs: result.fastestResponseTimeMs || 0,
                };

                return <SpatialPlanningResult result={data} />;
            }
            case 'VisualReactionResult': {
                const statistics = {
                    rounds: result.rounds.map((round, index) => ({
                        trials: round.questions.map((question, questionIndex) => ({
                            question: questionIndex + 1,
                            responseTimeMs: question.responseTimeMs,
                            isCorrect: question.isCorrect,
                            isMissedResponse: question.errorType === VisualReactionErrorType.Missed,
                        })),
                        averageResponseTimeMs: round.averageResponseTimeMsPerRound,
                        accuracy: round.accuracyPerRound,
                        fastestTimeMs: round.questions
                            .map(q => q.responseTimeMs)
                            .reduce((a, b) => Math.min(a, b), Infinity),
                        roundIndex: index,
                    })),
                    accuracy: result.accuracy,
                    averageResponseTimeMs: result.averageResponseTimeMs,
                    fastestResponseTimeMs: result.fastestResponseTimeMs,
                };

                return <VisualReactionResult statistics={statistics} />;
            }
            case 'TeamResilienceResult': {
                // Only include subcategory averages for the 'Team Resources' category
                const teamResources = result.subcategoryAverages.filter(s => s.category === 'Team Resources');

                const domainMax = Math.max(5, ...teamResources.map(s => s.maximumPossibleScore));
                const radarData = teamResources.map(s => ({
                    shortLabel: s.subcategory,
                    value: Number(s.averageScore.toFixed(2)),
                }));

                // Coarser interval to avoid cluttered labels (prefer steps of 25 for large scales, else 20)
                const step = domainMax >= 100 ? 25 : 20;
                const tickCount = domainMax <= 10 ? domainMax + 1 : Math.floor(domainMax / step) + 1;

                const summaryCards = [
                    ...teamResources.map(subcategory => ({
                        label: subcategory.subcategory,
                        value: Number(subcategory.averageScore.toFixed(2)),
                        total: subcategory.maximumPossibleScore,
                    })),
                ];

                const statistics = { domainMax, radarData, summaryCards, tickCount };

                return <TeamResilienceResult statistics={statistics} />;
            }
            case 'SelfDeterminationResult': {
                // Exclude the "Overall" category from the radar chart, but keep it for summaries
                const radarCategories = result.categoryAverages.filter(c => c.category !== 'Overall');

                const domainMax = Math.max(5, ...radarCategories.map(c => c.maximumPossibleScore));
                const radarData = radarCategories.map(c => ({
                    shortLabel: c.category,
                    value: Number(c.averageScore.toFixed(2)),
                }));

                const summaryCards = [
                    ...result.categoryAverages.map(c => ({
                        label: c.category,
                        value: Number(c.averageScore.toFixed(2)),
                        total: c.maximumPossibleScore,
                    })),
                ];
                const statistics = { domainMax, radarData, summaryCards };

                return <SelfDeterminationResult statistics={statistics} />;
            }
            case 'ML360Result': {
                const stats: ML360Statistic[] = result.categoryAverages.map(c => ({
                    label: c.category,
                    shortLabel: convertShortLabel(c.category),
                    value: c.averageScore.toFixed(1),
                    total: c.maxScore,
                }));
                const statistics: ML360Statistic[] = stats;

                return <ML360Result statistics={statistics} />;
            }
            default:
                return <div>{t('error.noResults')}</div>;
        }
    }, [result, t]);

    const renderTitle = useMemo(
        () => data?.assessmentResult?.moduleInstance?.name || '',
        [data?.assessmentResult?.moduleInstance?.name]
    );

    return (
        <Drawer
            open={open}
            onClose={onClose}
            title={t('details.title', { userName: data?.assessmentResult?.user?.name || '', title: renderTitle })}
            titleClassName="font-bold text-xl lg:text-2xl text-neutral-900"
        >
            {loading && <Loader />}

            {error && (
                <EmptyContainer>
                    <Empty title={t('error.noResults')} />
                </EmptyContainer>
            )}

            {!loading && !error && (
                <div>
                    <div className="text-base leading-6 font-semibold text-neutral-800">
                        {data?.assessmentResult?.assessmentRun?.program?.name}
                    </div>
                    <div className="pt-1">
                        <span className="pr-2 text-base leading-6 font-medium text-neutral-500">
                            {data?.assessmentResult?.assessmentRun?.name}
                        </span>
                        {data?.assessmentResult?.completedAt && (
                            <span className="border-l border-neutral-300 pl-2 text-base leading-6 font-medium text-neutral-500">
                                {t('details.completedOn', { date: formatDate(data.assessmentResult.completedAt) })}
                            </span>
                        )}
                    </div>
                    <div className="pt-5 lg:pt-10">{renderResult}</div>
                </div>
            )}
        </Drawer>
    );
};

export default ResultDrawer;
